import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowUpRight,
  ArrowDownRight,
  Users,
  MessageSquare,
  Eye,
  MousePointer,
  Sparkles,
  ThumbsUp,
  Send,
  BarChart,
  Percent,
  FileText,
  DollarSign,
  Hash,
  Loader2,
  Wand2,
  MinusCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SocialIcon, type SocialPlatform } from '../components/icons/SocialIcon';
import { DateRangePicker } from '../components/common/DateRangeSelector';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import { SocialMediaTabs } from '../components/common/SocialMediaTabs';
import { useAnalyticsSummary } from '../hooks/useAnalyticsSummary.ts'
import { usePlatformOverview } from '../hooks/usePlatformOverview.ts';
import { Brand } from '../types/analytics';
import { useAccounts } from '../hooks/useAccounts.ts';
import { LinkBrandRedirectionCard } from '../components/common/LinkBrandRedirectionCard';
import { useAnalyticsUrlState } from '../store/analyticsStore';
import { useSearchParams } from 'react-router-dom';
import { useBrands, type Brand as BrandsHookBrand, type BrandsResponse } from '../hooks/useBrands.ts';
import { useOnDemandAiAnalysis } from '../hooks/useOnDemandAiAnalysis.ts';
import type { AiAnalysisData } from '../services/analyticsService.ts';

// Define a type for connected pages (adjust to your actual data structure)
interface ConnectedPage {
  platform: SocialPlatform | string;
  brandId: string;
  pageId?: string;
  name?: string; 
  // Add other properties from your actual page object if needed
}

// Define platform names for display
interface Platform {
  id: string;
  name: string;
}

// Only show supported platforms in tabs (remove Twitter, TikTok, Pinterest, Google Business)
const platforms: Platform[] = [
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'youtube', name: 'YouTube' },
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook' }
];

// Define a type for our metrics
interface DisplayMetric {
  title: string;
  value: number;
  previousValue: number;
  change: number;
  icon: React.ElementType;
  color: string;
  rawValueChange?: string; // To display "∞" or the original percentage string
}

// Interface for metrics coming from the backend's keyMetrics and secondaryMetrics
interface BackendMetricItem {
  type: string;
  total: number;
  previousTotal: number;
  variationsPercentage: string;
}

// Mock data for fallback
const mockSummaryMetrics: DisplayMetric[] = [
  {
    title: 'Total Followers',
    value: 1823,
    previousValue: 1642,
    change: 11.02,
    icon: Users,
    color: 'bg-blue-500'
  },
  {
    title: 'Total Engagement',
    value: 3127,
    previousValue: 2819,
    change: 10.93,
    icon: ThumbsUp,
    color: 'bg-green-500'
  },
  {
    title: 'Total Posts',
    value: 58,
    previousValue: 62,
    change: -6.45,
    icon: FileText,
    color: 'bg-orange-500'
  },
  {
    title: 'Total Impressions',
    value: 27305,
    previousValue: 24112,
    change: 13.24,
    icon: Eye,
    color: 'bg-indigo-500'
  },
  {
    title: 'Avg. Engagement Rate',
    value: 6.93,
    previousValue: 5.72,
    change: 21.15,
    icon: Percent,
    color: 'bg-rose-500'
  }
];

// Mock AI insights for fallback
const mockAIInsights = {
  summary: "La performance globale sur toutes les plateformes s'est améliorée, avec une croissance notable des abonnés et de l'engagement. Bien que le nombre de publications ait légèrement diminué, l'efficacité du contenu s'est accrue significativement.",
  analysis: "L'augmentation de 29,59% des abonnés organiques indique une stratégie d'acquisition réussie, probablement due à un contenu plus engageant ou à une meilleure visibilité.",
  strengths: "Forte croissance des abonnés organiques, augmentation significative de l'engagement et des clics, amélioration du taux d'engagement et du CTR.",
  improvements: "Explorer les raisons de la diminution du nombre de publications et évaluer si cela affecte la croissance à long terme."
};

const Analytics = () => {
  const { t, i18n } = useTranslation();
  const lastMonth = subMonths(new Date(), 1);
  const [searchParams] = useSearchParams();
  const refreshQueryParam = searchParams.get('refresh');
  const [currentAiData, setCurrentAiData] = useState<AiAnalysisData | null>(null);
  
  // Use URL state management instead of local state
  const {
    activeTab,
    setActiveTab,
    selectedBrand,
    setSelectedBrand,
    dateRange,
    setDateRange,
    customDateRange,
    setCustomDateRange
  } = useAnalyticsUrlState();
  
  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform>('linkedin');

  // Fetch ALL brands for the dropdown
  const { data: allBrandsData, isLoading: isLoadingAllBrands } = useBrands();

  const processedAllBrands: Brand[] = useMemo(() => {
    const dataAsBrandsResponse = allBrandsData as (BrandsResponse | undefined);
    if (!dataAsBrandsResponse?.brands) return [];
    return dataAsBrandsResponse.brands.map((b: BrandsHookBrand) => ({
      id: b.id,
      name: b.name,
      logo: b.logo,
      website: b.website || '',
    }));
  }, [allBrandsData]);
  
  // Fetch accounts data to check platform availability
  const { data: accountsData, isLoading: isAccountsLoading } = useAccounts();
  const connectedPages: ConnectedPage[] = accountsData?.connected_pages || [];
  
  // Filter brands to only show those with connected accounts
  const brandsWithConnectedAccounts = useMemo(() => {
    if (!connectedPages.length) return processedAllBrands; // If no accounts, show all brands for connection
    
    // Get unique brand IDs from connected pages
    const brandIds = [...new Set(connectedPages.map(page => page.brandId).filter(Boolean))];
    
    // Filter brands to only include those with connected accounts
    return processedAllBrands.filter((brand: Brand) => brandIds.includes(brand.id));
  }, [processedAllBrands, connectedPages]);
  
  // Check if any accounts are connected
  const hasConnectedAccounts = connectedPages.length > 0;

  // Convert date range to string format for API
  const getFormattedDateRange = () => {
    if (dateRange === 'custom') {
      return {
        start: format(customDateRange.start, 'yyyy-MM-dd'),
        end: format(customDateRange.end, 'yyyy-MM-dd')
      };
    } else if (dateRange === 'this-month') {
      const now = new Date();
      return {
        start: format(startOfMonth(now), 'yyyy-MM-dd'),
        end: format(endOfMonth(now), 'yyyy-MM-dd')
      };
    } else if (dateRange === 'last-month') {
      return {
        start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
        end: format(endOfMonth(lastMonth), 'yyyy-MM-dd')
      };
    } else {
      // Handle numeric date range (last X days)
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - (dateRange as number));
      return {
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd')
      };
    }
  };

  const formattedDateRange = getFormattedDateRange();
  
  // Fetch analytics summary data
  const { 
    data: analyticsSummary, 
    isLoading: isSummaryLoading, 
    isError: isSummaryError, 
    error: summaryError 
  } = useAnalyticsSummary({
    start: formattedDateRange.start,
    end: formattedDateRange.end,
    brandId: selectedBrand || undefined,
    applanguage: i18n.language,
    enabled: activeTab === 'summary',
    refresh: refreshQueryParam,
    withAnalysis: false
  });

  // Check if the selected brand has an account for the active platform
  const hasPlatformAccount = useMemo(() => {
    if (activeTab === 'summary') return true; // Always show summary tab
    
    // If no brand is selected or "all brands" is selected
    if (!selectedBrand) return connectedPages.some((page: ConnectedPage) => page.platform === activeTab);
    
    // Check if the selected brand has an account for the active platform
    return connectedPages.some(
      (page: ConnectedPage) => page.platform === activeTab && page.brandId === selectedBrand
    );
  }, [activeTab, selectedBrand, connectedPages]);

  // Extract pageId for the selected brand and platform
  const getPageIdForSelectedBrand = useMemo(() => {
    if (!selectedBrand || activeTab === 'summary' || !connectedPages.length) return undefined;
    
    const platformAccount = connectedPages.find(
      (page: ConnectedPage) => page.platform === activeTab && page.brandId === selectedBrand
    );
    
    console.log('Selected Brand:', selectedBrand);
    console.log('Active Tab (Platform):', activeTab);
    console.log('Connected Pages:', connectedPages);
    console.log('Found Platform Account:', platformAccount);
    console.log('PageId for API call:', platformAccount?.pageId);
    
    return platformAccount?.pageId;
  }, [selectedBrand, activeTab, connectedPages]);

  // Fetch platform-specific data when a platform tab is selected
  const {
    data: platformData,
    isLoading: isPlatformLoading,
    isError: isPlatformError,
    error: platformError
  } = usePlatformOverview({
    platform: activeTab !== 'summary' ? activeTab as SocialPlatform : null,
    start: formattedDateRange.start,
    end: formattedDateRange.end,
    brandId: selectedBrand || undefined,
    pageId: getPageIdForSelectedBrand,
    hasPlatformAccount: hasPlatformAccount,
    refresh: refreshQueryParam,
    withAnalysis: false
  });

  // On-demand AI Analysis Mutation
  const onDemandAiMutation = useOnDemandAiAnalysis();

  // Effect to update currentAiData when analyticsSummary or platformData changes (initial load)
  useEffect(() => {
    // This effect primarily handles AI data that might come with the *initial* lean fetch.
    // AI data from the on-demand POST call will be set via the mutation's onSuccess.
    let initialAiData = null;
    if (activeTab === 'summary' && analyticsSummary?.ai?.summary) {
      initialAiData = analyticsSummary.ai;
    } else if (activeTab !== 'summary' && platformData?.ai?.summary) {
      initialAiData = platformData.ai;
    }
    // Only set if not already populated by a successful on-demand fetch
    // and if it's different from what might already be there.
    if (!onDemandAiMutation.isSuccess) { // Avoid overwriting successful on-demand AI with initial load AI
        setCurrentAiData(initialAiData);
    }

  }, [analyticsSummary, platformData, activeTab, onDemandAiMutation.isSuccess]);

  // Update available brands when data is loaded - This useEffect will be refactored/removed for populating the dropdown.
  // It will now focus on auto-selecting a brand if none is in the URL.
  useEffect(() => {
    let brandIdToSuggest: string | null = null;

    // Suggest a brand based on current API context (summary or platform)
    if (activeTab === 'summary' && analyticsSummary?.currentBrand?.id) {
      brandIdToSuggest = analyticsSummary.currentBrand.id;
    } else if (activeTab !== 'summary' && platformData?.currentBrand?.id) {
      brandIdToSuggest = platformData.currentBrand.id;
    }

    // If a brand is suggested by an API and no brand is currently selected in the URL state
    if (!selectedBrand && brandIdToSuggest) {
      // Check if the suggested brand ID exists in the list of brands with connected accounts
      const isValidSuggestion = brandsWithConnectedAccounts.some(b => b.id === brandIdToSuggest);
      if (isValidSuggestion) {
        setSelectedBrand(brandIdToSuggest);
      } else if (brandsWithConnectedAccounts.length > 0 && activeTab === 'summary') { 
        // Fallback for summary: if API suggestion is invalid or not present, but we have brands, 
        // consider selecting the first one. This part is debatable UX.
        // For now, let's be conservative and only set if the API-suggested one is valid and from summary context.
        // Or if no specific context, just ensure something is selected if possible.
        // setSelectedBrand(brandsWithConnectedAccounts[0].id); 
      }
    }
    // Note: The dropdown now uses `brandsWithConnectedAccounts` which only shows brands with connected accounts.
  }, [
    analyticsSummary, 
    platformData, 
    brandsWithConnectedAccounts, 
    activeTab, 
    selectedBrand, 
    setSelectedBrand
  ]);

  // Update selectedPlatform when activeTab changes
  useEffect(() => {
    if (activeTab !== 'summary' && activeTab !== 'all') {
      setSelectedPlatform(activeTab as SocialPlatform);
    }
  }, [activeTab]);

  // Helper function to dynamically generate platform-specific metric titles
  const getDynamicPlatformTitle = (type: string): string | null => {
    // Extract platform and metric type from backend metric types
    const platformFollowerPattern = /^(\w+)_FOLLOWERS$/;
    const platformSubscriberPattern = /^(\w+)_SUBSCRIBERS$/;
    
    const followerMatch = type.match(platformFollowerPattern);
    const subscriberMatch = type.match(platformSubscriberPattern);
    
    if (followerMatch) {
      const platform = followerMatch[1].toLowerCase();
      // Map platform codes to display names
      const platformNames: Record<string, string> = {
        'instagram': 'Instagram',
        'facebook': 'Facebook', 
        'linkedin': 'LinkedIn',
        'tiktok': 'TikTok',
        'twitter': 'Twitter'
      };
      
      const platformName = platformNames[platform];
      if (platformName) {
        return t('analytics.metrics.dynamicFollowers', { platform: platformName });
      }
    }
    
    if (subscriberMatch) {
      const platform = subscriberMatch[1].toLowerCase();
      const platformNames: Record<string, string> = {
        'youtube': 'YouTube',
        'linkedin': 'LinkedIn'
      };
      
      const platformName = platformNames[platform];
      if (platformName) {
        return t('analytics.metrics.dynamicSubscribers', { platform: platformName });
      }
    }
    
    return null;
  };

  // Helper to format metric type and get icon/color, now fully internationalized
  const getMetricDisplayDetails = (type: string) => {
    // First, check if this is a dynamic platform-specific metric
    const dynamicTitle = getDynamicPlatformTitle(type);
    
    // Map backend metric types to i18n keys
    const metricKeyMap: Record<string, string> = {
      FOLLOWERS: 'followers',
      TOTAL_FOLLOWERS: 'followers',
      TOTAL_FOLLOWERS_COUNT: 'followers',
      FOLLOWER_COUNT: 'followers',
      ORGANIC_FOLLOWERS: 'organicFollowers',
      PAID_FOLLOWERS: 'paidFollowers',
      POSTS: 'posts',
      TOTAL_POSTS: 'posts',
      IMPRESSIONS: 'impressions',
      TOTAL_IMPRESSIONS: 'impressions',
      UNIQUE_IMPRESSIONS: 'uniqueImpressions',
      CLICKS: 'clicks',
      REACTIONS: 'reactions',
      COMMENTS: 'comments',
      REPOSTS: 'reposts',
      CTR: 'ctr',
      ENGAGEMENT_RATE: 'engagementRate',
      AVG_ENGAGEMENT_RATE: 'engagementRate',
      PAGE_VIEWS: 'pageViews',
      UNIQUE_PAGE_VIEWS: 'uniquePageViews',
      VIEWS: 'views',
      LIKES: 'likes',
      DISLIKES: 'dislikes',
      ESTIMATEDMINUTESWATCHED: 'minutesWatched',
      AVERAGEVIEWDURATION: 'averageViewDuration',
      TOTAL_ENGAGEMENT: 'totalEngagement',
      NEW_FOLLOWERS: 'newFollowers',
      NEWSUBSCRIBERS: 'newSubscribers',
      PROFILE_VIEWS: 'profileViews',
      ACCOUNTS_ENGAGED: 'accountsEngaged',
      SAVES: 'saves',
      SHARES: 'shares',
      WEBSITE_CLICKS: 'websiteClicks',
      MEDIA_COUNT: 'mediaCount',
      REACH: 'reach',
      // Facebook UPPER_SNAKE_CASE metrics
      FAN_ADDS: 'fanAdds',
      FAN_REMOVES: 'fanRemoves',
      IMPRESSIONS_PAID: 'impressionsPaid',
      IMPRESSIONS_PAID_UNIQUE: 'impressionsPaidUnique',
      IMPRESSIONS_UNIQUE: 'impressionsUnique',
      IMPRESSIONS_VIRAL: 'impressionsViral',
      IMPRESSIONS_VIRAL_UNIQUE: 'impressionsViralUnique',
      POST_ENGAGEMENTS: 'postEngagements',
      POSTS_IMPRESSIONS: 'postImpressions',
      TOTAL_ACTIONS: 'totalActions',
      LOSTSUBSCRIBERS: 'lostSubscribers',
      SUBSCRIBERSGAIN: 'subscribersGain',
      CONNECTED_PLATFORMS: 'connectedPlatforms',
    };
    
    // Use dynamic title if available, otherwise use i18n key mapping
    const key = metricKeyMap[type] || type.replace(/_/g, '').toLowerCase();
    let title = dynamicTitle || t(`analytics.metrics.${key}`, key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()));
    
    if (type === 'NEW_FOLLOWERS') {
      title = t('analytics.metrics.followerGrowth', 'Follower Growth'); // Specific title for this metric type
    }

    switch (type) {
      case 'FOLLOWERS': case 'TOTAL_FOLLOWERS': case 'FOLLOWER_COUNT': case 'TOTAL_FOLLOWERS_COUNT': return { title, icon: Users, color: 'bg-blue-500' };
      case 'ORGANIC_FOLLOWERS': return { title, icon: Users, color: 'bg-green-500' };
      case 'PAID_FOLLOWERS': return { title, icon: DollarSign, color: 'bg-purple-500' };
      case 'POSTS': case 'TOTAL_POSTS': return { title, icon: FileText, color: 'bg-orange-500' };
      case 'IMPRESSIONS': case 'TOTAL_IMPRESSIONS': return { title, icon: Eye, color: 'bg-indigo-500' };
      case 'UNIQUE_IMPRESSIONS': return { title, icon: Eye, color: 'bg-cyan-500' };
      case 'CLICKS': return { title, icon: MousePointer, color: 'bg-emerald-500' };
      case 'REACTIONS': return { title, icon: ThumbsUp, color: 'bg-yellow-500' };
      case 'COMMENTS': return { title, icon: MessageSquare, color: 'bg-amber-500' };
      case 'REPOSTS': return { title, icon: Send, color: 'bg-red-500' };
      case 'CTR': return { title, icon: BarChart, color: 'bg-pink-500' };
      case 'ENGAGEMENT_RATE': case 'AVG_ENGAGEMENT_RATE': return { title, icon: Percent, color: 'bg-rose-500' };
      case 'PAGE_VIEWS': return { title, icon: FileText, color: 'bg-slate-500' };
      case 'UNIQUE_PAGE_VIEWS': return { title, icon: Hash, color: 'bg-gray-500' };
      case 'VIEWS': return { title, icon: Eye, color: 'bg-indigo-500' };
      case 'LIKES': return { title, icon: ThumbsUp, color: 'bg-green-500' };
      case 'DISLIKES': return { title, icon: MinusCircle, color: 'bg-red-500' };
      case 'ESTIMATEDMINUTESWATCHED': return { title, icon: BarChart, color: 'bg-purple-500' };
      case 'AVERAGEVIEWDURATION': return { title, icon: BarChart, color: 'bg-teal-500' };
      case 'TOTAL_ENGAGEMENT': return { title, icon: ThumbsUp, color: 'bg-green-500' };
      case 'NEWSUBSCRIBERS': return { title, icon: Users, color: 'bg-sky-500' };
      case 'ACCOUNTS_ENGAGED': return { title, icon: Users, color: 'bg-blue-400' };
      case 'MEDIA_COUNT': return { title, icon: FileText, color: 'bg-orange-400' };
      case 'PROFILE_VIEWS': return { title, icon: Eye, color: 'bg-cyan-400' };
      case 'REACH': return { title, icon: BarChart, color: 'bg-indigo-400' };
      case 'SAVES': return { title, icon: FileText, color: 'bg-green-400' };
      case 'SHARES': return { title, icon: Send, color: 'bg-purple-400' };
      case 'WEBSITE_CLICKS': return { title, icon: MousePointer, color: 'bg-pink-400' };
      // Facebook UPPER_SNAKE_CASE metrics
      case 'FAN_ADDS': case 'FAN_REMOVES': return { title, icon: Users, color: 'bg-blue-500' };
      case 'IMPRESSIONS_PAID': case 'IMPRESSIONS_PAID_UNIQUE': case 'IMPRESSIONS_UNIQUE':
      case 'IMPRESSIONS_VIRAL': case 'IMPRESSIONS_VIRAL_UNIQUE': return { title, icon: Eye, color: 'bg-indigo-500' };
      case 'POST_ENGAGEMENTS': return { title, icon: ThumbsUp, color: 'bg-green-500' };
      case 'POSTS_IMPRESSIONS': return { title, icon: Eye, color: 'bg-indigo-400' };
      case 'TOTAL_ACTIONS': return { title, icon: BarChart, color: 'bg-orange-500' };
      case 'LOSTSUBSCRIBERS': return { title, icon: Users, color: 'bg-red-500' };
      case 'SUBSCRIBERSGAIN': return { title, icon: Users, color: 'bg-green-500' };
      case 'CONNECTED_PLATFORMS': return { title, icon: FileText, color: 'bg-teal-500' };
      case 'NEW_FOLLOWERS': return { title, icon: Users, color: 'bg-sky-600' }; // Use specific title from above
      
      // Dynamic platform-specific cases (now handled by getDynamicPlatformTitle)
      case 'INSTAGRAM_FOLLOWERS': case 'FACEBOOK_FOLLOWERS': case 'LINKEDIN_FOLLOWERS': 
      case 'TIKTOK_FOLLOWERS': case 'TWITTER_FOLLOWERS': return { title, icon: Users, color: 'bg-pink-600' };
      case 'YOUTUBE_SUBSCRIBERS': return { title, icon: Users, color: 'bg-red-700' };
      
      default: 
        console.warn("Unknown metric type for display:", type);
        return { title, icon: BarChart, color: 'bg-gray-500' };
    }
  };

  const parseChangePercentage = (variationsPercentage: string | number): number => {
    if (typeof variationsPercentage === 'number') return variationsPercentage; // Already a number
    if (variationsPercentage === "∞" || variationsPercentage === "Infinity") return Infinity;
    if (variationsPercentage === "-∞" || variationsPercentage === "-Infinity") return -Infinity;
    const num = parseFloat(variationsPercentage);
    return isNaN(num) ? 0 : num;
  };

  // Generate summary metrics from API data
  const getSummaryMetrics = (): DisplayMetric[] => {
    const sourceKeyMetrics = (analyticsSummary as any)?.keyMetrics;

    if (!sourceKeyMetrics || !Array.isArray(sourceKeyMetrics) || sourceKeyMetrics.length === 0) {
      console.warn('Analytics summary data (analyticsSummary.keyMetrics) not available or empty. Using fallback mock data.');
      return mockSummaryMetrics.map(m => ({ 
        ...m,
        rawValueChange: m.change?.toString() ?? "0"
      }));
    }

    const keyMetrics = sourceKeyMetrics as BackendMetricItem[];
    const findMetric = (type: string): BackendMetricItem | undefined => keyMetrics.find(m => m.type === type);
    const metricsToDisplay: DisplayMetric[] = [];

    // Define the metrics we expect FOR THE KEY METRICS SECTION from the summary
    const summaryMetricDefinitions: Array<{ type: string; i18nKey: string; defaultTitle: string; icon: React.ElementType; color: string; oldMockTitle?: string }> = [
      {
        type: 'TOTAL_FOLLOWERS_COUNT',
        i18nKey: 'analytics.metrics.totalFollowersCount',
        defaultTitle: 'Total Followers',
        icon: Users,
        color: 'bg-blue-500',
        oldMockTitle: 'Total Followers'
      },
      // TOTAL_ENGAGEMENT is now in thirdMetrics
      // CONNECTED_PLATFORMS is now in thirdMetrics
      {
        type: 'VIEWS', // API provides 'VIEWS'
        i18nKey: 'analytics.metrics.views',
        defaultTitle: 'Total Views',
        icon: Eye,
        color: 'bg-indigo-500',
        oldMockTitle: 'Total Impressions'
      },
      {
        type: 'ENGAGEMENT_RATE', // Use ENGAGEMENT_RATE from keyMetrics
        i18nKey: 'analytics.metrics.engagementRate', // Ensure this i18n key covers general Engagement Rate
        defaultTitle: 'Engagement Rate',
        icon: Percent, 
        color: 'bg-rose-500',
        oldMockTitle: 'Avg. Engagement Rate' // Mock used 'Avg. Engagement Rate' 
      }
      // NEW_FOLLOWERS is now in thirdMetrics
    ];

    summaryMetricDefinitions.forEach(def => {
      const metric = findMetric(def.type);
      if (metric) {
        metricsToDisplay.push({
          title: t(def.i18nKey, def.defaultTitle),
          value: metric.total,
          previousValue: metric.previousTotal,
          change: parseChangePercentage(metric.variationsPercentage),
          icon: def.icon,
          color: def.color,
          rawValueChange: metric.variationsPercentage?.toString()
        });
      } else {
        console.warn(`Metric type "${def.type}" not found in summary keyMetrics. Card will not be displayed.`);
      }
    });

    // If after attempting to build from API, metricsToDisplay is empty, 
    // AND the API did return some keyMetrics (but none matched our definitions),
    // it's different from the API returning nothing. 
    // However, the current fallback above handles the API returning nothing.
    // If metricsToDisplay is empty here, it means specific expected metrics were not found.
    // This is now the desired behavior: show nothing for those missing expected metrics.

    return metricsToDisplay;
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleBrandChange = (brandId: string) => {
    setSelectedBrand(brandId);
  };

  const handleDateRangeChange = (newDateRange: 'this-month' | 'last-month' | number | 'custom') => {
    setDateRange(newDateRange);
  };

  const handleCustomDateRangeChange = (range: { start: Date; end: Date }) => {
    setCustomDateRange(range);
    setDateRange('custom');
  };

  // Modify getAIInsights to use the new structure
  const getAIInsights = () => {
    const noAIDataMessage = t('analytics.ai.noData', 'AI analysis is not available for this period.');
    const noStrengthsMessage = t('analytics.ai.noStrengths', 'No specific strengths identified yet.');
    // Using "weaknesses" for what was "improvements"
    const noWeaknessesMessage = t('analytics.ai.noImprovements', 'No specific weaknesses identified yet.'); 

    if ((analyticsSummary as any)?.ai?.error && !onDemandAiMutation.isPending && !currentAiData) {
      return {
        summary: t('analytics.ai.errorAnalysis', 'AI analysis encountered an error. You can try generating it again.'),
        strengths: [noStrengthsMessage],
        weaknesses: [noWeaknessesMessage],
        showRequestButton: true // Allow retry
      }
    }
    
    if (!currentAiData && !onDemandAiMutation.isPending) { 
        return {
            summary: t('analytics.ai.requestPrompt', 'Click to generate AI analysis.'),
            strengths: [noStrengthsMessage],
            weaknesses: [noWeaknessesMessage],
            showRequestButton: true 
        };
    }
    
    if (!currentAiData) { 
        return {
            summary: noAIDataMessage,
            strengths: [noStrengthsMessage],
            weaknesses: [noWeaknessesMessage],
            showRequestButton: false 
        };
    }
    
    // The API returns "strenghts", we map it to "strengths" for our internal use
    const strengthsList = currentAiData.strenghts && currentAiData.strenghts.length > 0 
      ? currentAiData.strenghts 
      : [noStrengthsMessage];
      
    const weaknessesList = currentAiData.weaknesses && currentAiData.weaknesses.length > 0
      ? currentAiData.weaknesses
      : [noWeaknessesMessage];

    return {
        summary: currentAiData.summary || noAIDataMessage,
        strengths: strengthsList,
        weaknesses: weaknessesList, // Mapped weaknesses to the 'improvements' UI slot
        showRequestButton: false
    };
  };

  const currentAIInsights = getAIInsights();

  const handleRequestAiAnalysis = () => {
    const sourceData = activeTab === 'summary' ? analyticsSummary : platformData;
    const currentFormattedDateRange = getFormattedDateRange();

    if (!sourceData) {
      console.error('Source data is not available for AI analysis.');
      // Attempt to set AI card to a state where user can request
      if (!currentAiData) { // Check if AI data is already absent
        setCurrentAiData(null); // Explicitly nullify to show request button via getAIInsights logic
      }
      return;
    }
    
    let metricsForAI: { type: string; total: number; previousTotal: number; variationsPercentage: string; }[] = [];

    if (activeTab === 'summary') {
      // Use keyMetrics from analyticsSummary for the new structure
      if (!analyticsSummary || !(analyticsSummary as any).keyMetrics) {
        console.error('Summary keyMetrics data is not available for AI analysis.');
        if (!currentAiData) {
            setCurrentAiData(null); 
        }
        return;
      }
      metricsForAI = (analyticsSummary as any).keyMetrics.map((metric: BackendMetricItem) => ({
        type: metric.type,
        total: metric.total,
        previousTotal: metric.previousTotal,
        variationsPercentage: metric.variationsPercentage?.toString() || "0", // Ensure it's a string
      }));
    } else if (platformData) {
      // Cast platformData to include keyMetrics and secondaryMetrics, assuming new API structure
      const typedPlatformData = platformData as any as { keyMetrics?: BackendMetricItem[], secondaryMetrics?: BackendMetricItem[] };
      
      if (!typedPlatformData.keyMetrics && !typedPlatformData.secondaryMetrics) {
        console.error('Platform metrics data (keyMetrics or secondaryMetrics) is not available for AI analysis.');
        return;
      }
      metricsForAI = (typedPlatformData.keyMetrics || []).concat(typedPlatformData.secondaryMetrics || []).map((metric: BackendMetricItem) => {
        return {
          type: metric.type, // Already in correct format (e.g., FOLLOWERS)
          total: metric.total,
          previousTotal: metric.previousTotal,
          variationsPercentage: metric.variationsPercentage,
        };
      });
    } else {
      console.error('No data source available for AI analysis.');
      return;
    }
    
    if (!metricsForAI || metricsForAI.length === 0) {
      console.error('No valid metrics found for AI analysis.');
      return;
    }

    const currentTimestamps = {
      start: new Date(currentFormattedDateRange.start).getTime(),
      end: new Date(currentFormattedDateRange.end).getTime(),
    };

    // Construct the inner data object based on the curl example
    // Omitting 'expiresAt', 'payload.success', and 'debug.previousTimeframe' for now
    // as their source/necessity is unclear.
    const innerDataObject = {
      payload: {
        metrics: metricsForAI,
        debug: {
          currentTimeframe: currentTimestamps,
          // previousTimeframe: {} // Leaving out previousTimeframe for now
        }
      }
    };

    const dataStringForApi = JSON.stringify(innerDataObject);
    
    onDemandAiMutation.mutate({
      appLanguage: i18n.language || 'en',
      platform: activeTab === 'summary' ? undefined : activeTab as SocialPlatform, 
      data: dataStringForApi, 
    }, {
      onSuccess: (response) => {
        console.log('On-demand AI Analysis successful:', response);
        setCurrentAiData(response.analysis); 
      },
      onError: (error) => {
        console.error('On-demand AI Analysis error:', error);
        alert(t('analytics.ai.errorOnDemand', 'Failed to get AI analysis. Please try again.'));
        setCurrentAiData(null); 
      }
    });
  };
  
  // Define a comprehensive loading state for the main page content
  const isPageLoading = (activeTab === 'summary' && isSummaryLoading) || 
                        (activeTab !== 'summary' && isPlatformLoading) || 
                        isAccountsLoading || isLoadingAllBrands;

  // Loading state for the AI section specifically when on-demand AI is being fetched.
  const isAiCardLoading = onDemandAiMutation.isPending;

  // Determine if there's an error for the main page data
  const isError = (activeTab === 'summary' && isSummaryError) || 
                 (activeTab !== 'summary' && isPlatformError && hasPlatformAccount);
  
  // Get the appropriate error message
  const errorMessage = activeTab === 'summary' 
    ? summaryError?.message 
    : platformError?.message;

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      <div className="w-[95%] mx-auto">
        <div className="py-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-semibold text-gray-900 font-title">
                {t('analytics.title')}
              </h1>
              <p className="mt-1 text-sm text-gray-500 font-body">
                {t('analytics.subtitle')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <BrandDropdown
                brands={brandsWithConnectedAccounts || []}
                selectedBrandId={selectedBrand || ''}
                onBrandSelect={handleBrandChange}
                showRedirectionCard={false}
                isLoading={isLoadingAllBrands}
              />

              <DateRangePicker
                dateRange={dateRange}
                customDateRange={customDateRange}
                onDateRangeChange={handleDateRangeChange}
                onCustomDateRangeChange={handleCustomDateRangeChange}
                baseDate={new Date()}
              />
            </div>
          </div>

          <div className="mb-6">
            <SocialMediaTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              showSummary={true}
            />
          </div>

          {/* Show redirection card if no accounts are connected */}
          {!isAccountsLoading && !hasConnectedAccounts ? (
            <div className="mb-8">
              <LinkBrandRedirectionCard 
                message={t('analytics.connectAccountFirst', 'Connect a social media account to view analytics')}
                isLoading={isAccountsLoading}
              />
            </div>
          ) : activeTab !== 'summary' && !hasPlatformAccount ? (
            <div className="mb-8">
              <LinkBrandRedirectionCard 
                message={t('analytics.connectAccountForPlatform', {
                  platform: platforms.find(p => p.id === activeTab)?.name || activeTab
                })}
                isLoading={isAccountsLoading || isPlatformLoading}
              />
            </div>
          ) : isPageLoading ? (
            <div className="space-y-6">
              {/* Skeleton loader for the AI insights card */}
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-3">
                  <div className="rounded-xl border border-black shadow-lg bg-white overflow-hidden h-full animate-pulse">
                    <div className="p-6 border-b border-gray-200 bg-gray-100">
                      <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                    </div>
                    <div className="p-6">
                      <div className="flex flex-col">
                        <div className="mb-6">
                          <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
                          <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                        </div>
                        
                        <div className="flex gap-4">
                          <div className="flex-1 border-2 border-gray-100 rounded-lg p-4">
                            <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
                            <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                            <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                          </div>
                          
                          <div className="flex-1 border-2 border-gray-100 rounded-lg p-4">
                            <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
                            <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                            <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Skeleton loaders for metric cards */}
                  {Array(2).fill(0).map((_, index) => (
                    <div key={index} className="rounded-xl border border-black shadow-lg bg-white p-4 animate-pulse">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="h-5 w-5 bg-gray-200 rounded-full"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      </div>
                      <div className="space-y-3">
                        {Array(3).fill(0).map((_, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="h-2 bg-gray-200 rounded w-1/4"></div>
                            <div className="h-2 bg-gray-200 rounded w-1/6"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Skeleton loader for metrics grid */}
              <div className="space-y-6 pb-8">
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {Array(5).fill(0).map((_, index) => (
                    <div key={index} className="rounded-xl p-4 border border-black shadow-lg bg-white animate-pulse">
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-8 w-8 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-6 bg-gray-200 rounded w-2/3"></div>
                        <div className="h-2 bg-gray-200 rounded w-1/3"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : isError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <h3 className="text-red-800 font-medium">Error loading analytics data</h3>
              <p className="text-red-600 text-sm mt-1">{errorMessage || 'An unknown error occurred'}</p>
            </div>
          ) : (
            <>
              <div className="mb-8 grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-3">
                  <div className="rounded-xl border border-black shadow-lg bg-white overflow-hidden h-full">
                    <div className="p-6 border-b border-gray-200" style={{ backgroundColor: "#F2F0FD" }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Sparkles className="w-6 h-6 text-indigo-600" />
                          <h2 className="text-lg font-semibold text-gray-900 font-title">
                            {activeTab === 'summary' 
                              ? t('analytics.ai.title', 'AI Performance Analysis') 
                              : (
                                <div className="flex items-center">
                                  <SocialIcon 
                                    platform={selectedPlatform} 
                                    size={20} 
                                    className="mr-2" 
                                  />
                                  {t('analytics.ai.platformAnalysisTitle', `${platforms.find(p => p.id === activeTab)?.name || 'Platform'} Analysis`, { platform: platforms.find(p => p.id === activeTab)?.name || 'Platform' })}
                                </div>
                              )}
                          </h2>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      {(currentAIInsights.showRequestButton) ? (
                        <div className="flex flex-col items-center justify-center text-center py-8">
                          <Wand2 className="w-12 h-12 text-indigo-400 mb-4" />
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {t('analytics.ai.getAnalysisTitleGeneral', 'Get AI Analysis')}
                          </h3>
                          <p className="text-gray-600 mb-6 max-w-md">
                            {t('analytics.ai.getAnalysisDescriptionNew', 'Unlock AI-powered insights about your social media actions to better focus on what works and improve your social media strategy.')}
                          </p>
                          <button
                            onClick={handleRequestAiAnalysis}
                            disabled={isAiCardLoading}
                            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-indigo-400"
                          >
                            {isAiCardLoading ? (
                              <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                {t('analytics.ai.analyzing', 'Analyzing...')}
                              </>
                            ) : (
                              <>
                                <Wand2 className="w-5 h-5 mr-2" />
                                {t('analytics.ai.analyzeButton', 'Analyze Now')}
                              </>
                            )}
                          </button>
                        </div>
                      ) : isAiCardLoading ? (
                        <div className="animate-pulse">
                          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                          <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6 mb-6"></div>
                          <div className="flex gap-4">
                            <div className="flex-1 border-2 border-gray-100 rounded-lg p-4">
                              <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
                              <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                              <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                            </div>
                            <div className="flex-1 border-2 border-gray-100 rounded-lg p-4">
                              <div className="h-3 bg-gray-200 rounded w-1/3 mb-3"></div>
                              <div className="h-2 bg-gray-200 rounded w-full mb-2"></div>
                              <div className="h-2 bg-gray-200 rounded w-4/5"></div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-indigo-900 mb-3">{t('analytics.ai.summary','Summary')}</h3>
                            <p className="text-gray-700 whitespace-pre-line">{currentAIInsights.summary}</p>
                          </div>
                          
                          <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1 border-2 border-green-100 rounded-lg p-4">
                              <h3 className="text-sm font-medium text-green-600 mb-2 flex items-center whitespace-nowrap">
                                <div className="w-1 h-4 bg-green-500 mr-2 rounded-full"></div>
                                {t('analytics.ai.strengths', 'Points Forts')}
                              </h3>
                              {currentAIInsights.strengths.length > 0 ? (
                                <ul className="list-disc list-inside text-gray-700 space-y-1">
                                  {currentAIInsights.strengths.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-700">{t('analytics.ai.noStrengths', 'No specific strengths identified yet.')}</p>
                              )}
                            </div>
                            
                            <div className="flex-1 border-2 border-orange-100 rounded-lg p-4">
                              <h3 className="text-sm font-medium text-orange-600 mb-2 flex items-center whitespace-nowrap">
                                <div className="w-1 h-4 bg-orange-500 mr-2 rounded-full"></div>
                                {t('analytics.ai.improvements', 'Axes d\'Amélioration')} 
                              </h3>
                              {currentAIInsights.weaknesses.length > 0 ? (
                                <ul className="list-disc list-inside text-gray-700 space-y-1">
                                  {currentAIInsights.weaknesses.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-700">{t('analytics.ai.noImprovements', 'No specific areas for improvement identified yet.')}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Main Metrics */}
                <div className="space-y-4">
                  {(activeTab === 'summary' ? getSummaryMetrics() : 
                    ((platformData as any)?.keyMetrics || [] as BackendMetricItem[]).map((metric: BackendMetricItem) => {
                    const details = getMetricDisplayDetails(metric.type);
                    const change = parseChangePercentage(metric.variationsPercentage);
                    return {
                      title: details.title,
                      value: metric.total,
                      previousValue: metric.previousTotal,
                      change: change,
                      icon: details.icon,
                      color: details.color,
                      rawValueChange: metric.variationsPercentage?.toString()
                    } as DisplayMetric; // Ensure the returned object matches DisplayMetric
                  })).map((metric: DisplayMetric, index: number) => (
                    <div key={index} className="rounded-xl border border-black shadow-lg bg-white p-4">
                      <div className="flex items-center justify-between">
                        <div className={`${metric.color} bg-opacity-10 p-2 rounded-lg`}>
                          <metric.icon className={`w-5 h-5 ${metric.color.replace('bg-', 'text-')}`} />
                        </div>
                        <div className="flex items-center space-x-1">
                          <span className={`text-xs font-medium ${
                            metric.change > 0 ? 'text-green-600' : metric.change === 0 ? 'text-gray-600' : metric.change < 0 ? 'text-red-600' : 'text-gray-600' // Handle Infinity/NaN
                          }`}>
                            {isFinite(metric.change) ? 
                              `${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%` : 
                              (metric.rawValueChange || (metric.change > 0 ? '∞' : metric.change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))
                            }
                          </span>
                          {isFinite(metric.change) && metric.change !== 0 ? (
                            metric.change > 0 ? (
                              <ArrowUpRight size={12} className="text-green-600" />
                            ) : (
                              <ArrowDownRight size={12} className="text-red-600" />
                            )
                          ) : metric.rawValueChange === "∞" || metric.change === Infinity ? (
                             <ArrowUpRight size={12} className="text-green-600" />
                          ) : metric.rawValueChange === "-∞" || metric.change === -Infinity ? (
                             <ArrowDownRight size={12} className="text-red-600" />
                          ) : metric.rawValueChange === "0" && metric.change === 0 ? null /* Explicitly no icon for 0% exact */ : 
                             (metric.rawValueChange || metric.change !== 0) ? null : /* No icon if change is NaN but not explicitly 0 */ null }
                        </div>
                      </div>
                      <div className="mt-3">
                        <h3 className="text-base font-semibold text-gray-500 font-body">{metric.title}</h3>
                        <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                          {metric.title && (metric.title.includes('Rate') || metric.title.includes('CTR') || metric.title.includes('Avg. View Duration')) ? `${metric.value.toFixed(2)}${metric.title.includes('Rate') || metric.title.includes('CTR') ? '%' : ''}` : metric.value.toLocaleString()}
                        </p>
                        <p className="mt-1 text-xs text-gray-400">
                          {t('common.previous')}: {typeof metric.previousValue === 'number' ? metric.previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secondary Metrics */}
              {( 
                (activeTab === 'summary' && (analyticsSummary as any)?.secondaryMetrics && ((analyticsSummary as any).secondaryMetrics as BackendMetricItem[]).length > 0) ||
                (activeTab !== 'summary' && (platformData as any)?.secondaryMetrics && ((platformData as any).secondaryMetrics as BackendMetricItem[]).length > 0)
              ) && (
                <div className="space-y-6 pb-8">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {activeTab === 'summary' ? (
                        t('analytics.specificPlatformsMetricsTitle', 'Specific Platforms Metrics') // This was recently updated, confirming it's internationalized
                      ) : (
                        <div className="flex items-center">
                          <SocialIcon 
                            platform={selectedPlatform} 
                            size={20} 
                            className="mr-2" 
                          />
                          {platforms.find(p => p.id === activeTab)?.name || 'Platform'} Metrics
                        </div>
                      )}
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    { (activeTab === 'summary' 
                        ? ((analyticsSummary as any).secondaryMetrics || [])
                        : ((platformData as any).secondaryMetrics || [])
                      )
                      .map((metric: BackendMetricItem) => {
                      const details = getMetricDisplayDetails(metric.type);
                      const change = parseChangePercentage(metric.variationsPercentage);
                      return {
                        // Cast metric.platform to SocialPlatform for SocialIcon
                        ...metric, // Spread original metric properties like platform, total, etc.
                        title: details.title,
                        value: metric.total, // Already present in metric, but ensure it's used
                        previousValue: metric.previousTotal, // Already present
                        change: change,
                        icon: details.icon, // Will be overridden by SocialIcon if platform exists
                        color: details.color,
                        rawValueChange: metric.variationsPercentage?.toString()
                      } as DisplayMetric & { platform?: string }; // Extend DisplayMetric for platform
                    }).map((metric: DisplayMetric & { platform?: string }, index: number) => (
                      <div 
                        key={index} 
                        className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white"
                      >
                        <div className="flex items-center justify-between">
                          <div className={`${metric.color} bg-opacity-10 p-2 rounded-lg`}>
                            {metric.platform ? (
                              <SocialIcon platform={metric.platform as SocialPlatform} size={20} />
                            ) : (
                              <metric.icon className={`w-5 h-5 ${metric.color.replace('bg-', 'text-')}`} />
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                             <span className={`text-xs font-medium ${
                              metric.change > 0 ? 'text-green-600' : metric.change === 0 ? 'text-gray-600' : metric.change < 0 ? 'text-red-600' : 'text-gray-600' // Handle Infinity/NaN
                            }`}>
                              {isFinite(metric.change) ? 
                                `${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%` : 
                                (metric.rawValueChange || (metric.change > 0 ? '∞' : metric.change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))
                              }
                            </span>
                            {isFinite(metric.change) && metric.change !== 0 ? (
                              metric.change > 0 ? (
                                <ArrowUpRight size={12} className="text-green-600" />
                              ) : (
                                <ArrowDownRight size={12} className="text-red-600" />
                              )
                            ) : metric.rawValueChange === "∞" || metric.change === Infinity ? (
                               <ArrowUpRight size={12} className="text-green-600" />
                            ) : metric.rawValueChange === "-∞" || metric.change === -Infinity ? (
                               <ArrowDownRight size={12} className="text-red-600" />
                            ) : null}
                          </div>
                        </div>
                        <div className="mt-3">
                          <h3 className="text-base font-semibold text-gray-500 font-body">{metric.title}</h3>
                          <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                             {metric.title && (metric.title.includes('Rate') || metric.title.includes('CTR') || metric.title.includes('Avg. View Duration')) ? `${metric.value.toFixed(2)}${metric.title.includes('Rate') || metric.title.includes('CTR') ? '%' : ''}` : metric.value.toLocaleString()}
                          </p>
                          <p className="mt-1 text-xs text-gray-400">
                            {t('common.previous')}: {typeof metric.previousValue === 'number' ? metric.previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Third Metrics - New Section */}
              {activeTab === 'summary' && (analyticsSummary as any)?.thirdMetrics && ((analyticsSummary as any).thirdMetrics as BackendMetricItem[]).length > 0 && (
                <div className="space-y-6 pb-8">
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-semibold text-gray-900">
                      {t('analytics.thirdMetricsSummaryTitle', 'Other relevant metrics')}
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    { ((analyticsSummary as any).thirdMetrics || [] as BackendMetricItem[])
                      .map((metric: BackendMetricItem) => {
                        // Process metric for display (similar to secondary metrics)
                        const details = getMetricDisplayDetails(metric.type);
                        const change = parseChangePercentage(metric.variationsPercentage);
                        return {
                          ...metric, // Spread original metric properties
                          title: details.title,
                          value: metric.total,
                          previousValue: metric.previousTotal,
                          change: change,
                          icon: details.icon, // Fallback icon
                          color: details.color,
                          rawValueChange: metric.variationsPercentage?.toString()
                        } as DisplayMetric & { platform?: string };
                      })
                      .map((metric: DisplayMetric & { platform?: string }, index: number) => (
                        <div 
                          key={index} 
                          className="rounded-xl p-4 border border-black shadow-lg hover:shadow-xl transition-shadow bg-white"
                        >
                          <div className="flex items-center justify-between">
                            <div className={`${metric.color} bg-opacity-10 p-2 rounded-lg`}>
                              {metric.platform ? (
                                <SocialIcon platform={metric.platform as SocialPlatform} size={20} />
                              ) : (
                                <metric.icon className={`w-5 h-5 ${metric.color.replace('bg-', 'text-')}`} />
                              )}
                            </div>
                            <div className="flex items-center space-x-1">
                               <span className={`text-xs font-medium ${metric.change > 0 ? 'text-green-600' : metric.change === 0 ? 'text-gray-600' : metric.change < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                {isFinite(metric.change) ? 
                                  `${metric.change > 0 ? '+' : ''}${metric.change.toFixed(1)}%` : 
                                  (metric.rawValueChange || (metric.change > 0 ? '∞' : metric.change < 0 ? '-∞' : t('common.notAvailable', 'N/A')))}
                              </span>
                              {isFinite(metric.change) && metric.change !== 0 ? (
                                metric.change > 0 ? (
                                  <ArrowUpRight size={12} className="text-green-600" />
                                ) : (
                                  <ArrowDownRight size={12} className="text-red-600" />
                                )
                              ) : metric.rawValueChange === "∞" || metric.change === Infinity ? (
                                 <ArrowUpRight size={12} className="text-green-600" />
                              ) : metric.rawValueChange === "-∞" || metric.change === -Infinity ? (
                                 <ArrowDownRight size={12} className="text-red-600" />
                              ) : metric.rawValueChange === "0" && metric.change === 0 ? null 
                                 : (metric.rawValueChange || metric.change !== 0) ? null : null }
                            </div>
                          </div>
                          <div className="mt-3">
                            <h3 className="text-base font-semibold text-gray-500 font-body">{metric.title}</h3>
                            <p className="mt-1 text-2xl font-semibold text-gray-900 font-title">
                               {metric.title && (metric.title.includes('Rate') || metric.title.includes('CTR') || metric.title.includes('Avg. View Duration')) ? `${metric.value.toFixed(2)}${metric.title.includes('Rate') || metric.title.includes('CTR') ? '%' : ''}` : metric.value.toLocaleString()}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              {t('common.previous')}: {typeof metric.previousValue === 'number' ? metric.previousValue.toLocaleString() : t('common.notAvailable', 'N/A')}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;